import { FetchKey } from '@app/src/api/fetchHooks'
import { useAccount } from '@app/src/context/AccountContext'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import { useSessionNavigation } from '@app/src/context/SessionNavigationContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import { clearPersistedMrtTableState } from '@app/src/pages/ResourceCollection/MrtCollection/mrtTableStateStorage'
import { toError } from '@app/src/errors/toError'
import sentryService from '@app/src/services/SentryService'
import { NotificationSeverity } from '@app/src/wf-constants'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'

export type Login = (
  role: string,
  solution: string,
  organization: number,
  redirect?: (solution: string) => void,
) => Promise<void>

const useLogin = (): Login => {
  const { formatMessage } = useIntl()
  const { getToken } = useAuthentication()
  const { fetchAccount } = useAccount()
  const { beginSessionNavigation, endSessionNavigation } = useSessionNavigation()
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()

  return useCallback(
    async (role, solution: string, organization: number, redirect?: (solution: string) => void) => {
      beginSessionNavigation()
      try {
        const token = await getToken({ role, solution: solution, organization })
        await fetchAccount()
        if (token) {
          const accountData = queryClient.getQueryData([FetchKey.Account])
          queryClient.clear()
          clearPersistedMrtTableState()
          if (accountData) {
            queryClient.setQueryData([FetchKey.Account], accountData)
          }
          redirect && redirect(solution)
        } else {
          throw new Error('err_selecting_solution')
        }
      } catch (e) {
        sentryService.captureError(toError(e, 'Solution selection error'), {
          tags: {
            errorType: 'solution-selection',
          },
          extra: {
            solution,
            error: e,
            errorDetails: e,
          },
          level: 'error',
        })
        console.error(e)
        showSnackbar({
          message: formatMessage({ id: 'error.err_selecting_solution' }),
          severity: NotificationSeverity.error,
        })
      } finally {
        endSessionNavigation()
      }
    },
    [beginSessionNavigation, endSessionNavigation, fetchAccount, getToken, queryClient, showSnackbar, formatMessage],
  )
}

export default useLogin
